import { memo, useMemo, useState } from 'react';

import { type Board } from '@air/api/types';
import { itemIsHighlightedOnShiftSelectSelector } from '@air/redux-selected-items';

import { type ItemProps } from '~/components/Gallery/types';
import { BoardCellThumbnail } from '~/components/TableView/NameCell/BoardCellThumbnail';
import { NameCellContent } from '~/components/TableView/NameCell/NameCellContent';
import { DropdownMenuWrapper } from '~/components/TableView/PrivateTableView/DropdownMenuWrapper';
import { PrivateBoardNameCellTitle } from '~/components/TableView/PrivateTableView/PrivateBoardNameCellTitle';
import { BOARD_NAME_CELL } from '~/constants/testIDs';
import { useAirSelector } from '~/utils/ReduxUtils';
import { useIsItemRenaming } from './TableViewItemRenamingContext';
import { useEscapeKeyHandler } from './useEscapeKeyHandler';

export type PrivateBoardNameCellProps = {
  isHovering: boolean;
  isSelected: boolean;
  board: Board;
} & ItemProps;

export const PrivateBoardNameCell = memo(
  ({
    isHovering,
    isSelected,
    board,
    getItemMenuOptions,
    getSelectionMenuOptions,
    onItemClick,
    onItemCmdClick,
  }: PrivateBoardNameCellProps) => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const isHighlightedFromShiftSelect = useAirSelector((st) => itemIsHighlightedOnShiftSelectSelector(st, board.id));
    const isCurrentlyRenaming = useIsItemRenaming(board.id);

    const _title = useMemo(() => {
      return <PrivateBoardNameCellTitle board={board} onClick={onItemClick} onCmdClick={onItemCmdClick} />;
    }, [board, onItemClick, onItemCmdClick]);

    const _image = useMemo(() => {
      return <BoardCellThumbnail thumbnail={board.thumbnails?.[0]} title={board.title} />;
    }, [board]);

    const ellipsisMenuOptions = useMemo(
      () => getItemMenuOptions({ trackLocation: 'table-item-ellipsis-menu' }),
      [getItemMenuOptions],
    );

    const selectionMenuOptions = getSelectionMenuOptions('table-multi-select-ellipsis-menu');
    const menuOptions = isSelected && selectionMenuOptions.length ? selectionMenuOptions : ellipsisMenuOptions;

    const shouldShowMenu = useMemo(() => {
      if (isCurrentlyRenaming === true) {
        return false;
      }

      if (isHighlightedFromShiftSelect === false) {
        if (isHovering === true || menuIsOpen === true) {
          return true;
        }
      }

      return false;
    }, [isCurrentlyRenaming, isHighlightedFromShiftSelect, isHovering, menuIsOpen]);

    const onEscapeKeyDown = useEscapeKeyHandler(shouldShowMenu, isSelected, menuIsOpen, board);

    return (
      <NameCellContent data-testid={BOARD_NAME_CELL} title={_title} image={_image}>
        <div className="flex items-center gap-1">
          {shouldShowMenu && !!menuOptions.length && (
            <DropdownMenuWrapper
              options={menuOptions}
              onOpenChange={setMenuIsOpen}
              onEscapeKeyDown={onEscapeKeyDown}
              dataTestId={`${BOARD_NAME_CELL}-menu`}
            />
          )}
        </div>
      </NameCellContent>
    );
  },
);

PrivateBoardNameCell.displayName = 'PrivateBoardNameCell';
